import React, {
  useRef,
  useEffect,
  useCallback,
  useImperativeHandle,
  forwardRef,
  useState,
} from 'react';

// ── Config (matched to designer prototype) ─────────────────────────
const CANVAS_PAD = 120;

const CONFIG = {
  sampleGap: 5,
  maxParticles: 3800,
  particleSize: 2.6,
  dissolveMs: 1200,
  reformMs: 1200,
  autoHoldMs: 60000,
  swirl: 50,
  scatter: 54,
  sparkleCount: 110,
  holdDrift: 9,
};

// ── Types ───────────────────────────────────────────────────────────
interface Particle {
  sx: number;
  sy: number;
  ex: number;
  ey: number;
  mx: number;
  my: number;
  sr: number;
  sg: number;
  sb: number;
  sa: number;
  er: number;
  eg: number;
  eb: number;
  ea: number;
  size: number;
  twinkle: number;
  delay: number;
  spin: number;
  seed: number;
  holdAngle: number;
  holdRadius: number;
}

interface Glint {
  angle: number;
  radius: number;
  size: number;
  speed: number;
  phase: number;
  hueMix: number;
}

export interface DreamieTransitionHandle {
  dissolve: () => void;
  reformTo: (url: string) => void;
  /** Jump straight to the swirling-particle hold state (no dissolve animation). */
  startInHold: () => void;
}

interface Props {
  imageUrl: string;
  alt: string;
  onReformComplete?: () => void;
  width?: number;
  height?: number;
  /** When true, the static <img> starts hidden (for startInHold workflows). */
  startHidden?: boolean;
}

// ── Math helpers (exact match to prototype) ─────────────────────────
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const clamp = (v: number, min: number, max: number) =>
  Math.max(min, Math.min(max, v));
const smoothstep = (min: number, max: number, v: number) => {
  const t = clamp((v - min) / (max - min), 0, 1);
  return t * t * (3 - 2 * t);
};
const easeInOutCubic = (t: number) =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
const bell = (t: number, start: number, end: number) => {
  if (t <= start || t >= end) return 0;
  const mid = (start + end) / 2;
  return t < mid ? smoothstep(start, mid, t) : 1 - smoothstep(mid, end, t);
};
const quadBezier = (a: number, b: number, c: number, t: number) => {
  const mt = 1 - t;
  return mt * mt * a + 2 * mt * t * b + t * t * c;
};
const shuffle = <T,>(arr: T[]): T[] => {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = (Math.random() * (i + 1)) | 0;
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
};
const coverRect = (srcW: number, srcH: number, maxW: number, maxH: number) => {
  const scale = Math.max(maxW / srcW, maxH / srcH);
  return { w: srcW * scale, h: srcH * scale };
};

// ── Image → particle points ─────────────────────────────────────────
function imageToParticlePoints(
  img: HTMLImageElement,
  baseW: number,
  baseH: number,
  gap = CONFIG.sampleGap,
) {
  const off = document.createElement('canvas');
  off.width = baseW;
  off.height = baseH;
  const octx = off.getContext('2d', { willReadFrequently: true })!;
  const fit = coverRect(img.width, img.height, baseW, baseH);
  const x = (baseW - fit.w) / 2;
  const y = (baseH - fit.h) / 2;
  octx.clearRect(0, 0, baseW, baseH);
  octx.drawImage(img, x, y, fit.w, fit.h);

  let data: Uint8ClampedArray | null = null;
  try {
    data = octx.getImageData(0, 0, baseW, baseH).data;
  } catch {
    // Canvas tainted (CORS) — fall back to grid of neutral-toned particles
  }

  const points: {
    x: number;
    y: number;
    r: number;
    g: number;
    b: number;
    a: number;
  }[] = [];

  if (data) {
    for (let py = 0; py < baseH; py += gap) {
      for (let px = 0; px < baseW; px += gap) {
        const i = (py * baseW + px) * 4;
        const a = data[i + 3];
        if (a > 24) {
          points.push({
            x: px,
            y: py,
            r: data[i],
            g: data[i + 1],
            b: data[i + 2],
            a: a / 255,
          });
        } else {
          // Transparent region — fill with soft neutral particles
          const tone = 200 + Math.floor(Math.random() * 40);
          points.push({ x: px, y: py, r: tone, g: tone, b: tone, a: 0.35 });
        }
      }
    }
  } else {
    // Tainted canvas fallback: generate particles covering the drawn area
    const drawX = Math.round(x);
    const drawY = Math.round(y);
    const drawW = Math.round(fit.w);
    const drawH = Math.round(fit.h);
    for (let py = drawY; py < drawY + drawH; py += gap) {
      for (let px = drawX; px < drawX + drawW; px += gap) {
        const tone = 140 + Math.floor(Math.random() * 80);
        points.push({ x: px, y: py, r: tone, g: tone, b: tone, a: 0.85 });
      }
    }
  }
  shuffle(points);
  if (points.length > CONFIG.maxParticles) points.length = CONFIG.maxParticles;
  return points;
}

function buildMorphParticles(
  aImg: HTMLImageElement,
  bImg: HTMLImageElement,
  baseW: number,
  baseH: number,
): Particle[] {
  const a = imageToParticlePoints(aImg, baseW, baseH);
  const b = imageToParticlePoints(bImg, baseW, baseH);
  const count = Math.max(a.length, b.length);
  const out: Particle[] = new Array(count);
  for (let i = 0; i < count; i++) {
    const p1 = a[i % a.length];
    const p2 = b[i % b.length];
    const angle = Math.random() * Math.PI * 2;
    const dist = CONFIG.scatter * (0.35 + Math.random() * 0.9);
    const midBias = (Math.random() - 0.5) * CONFIG.swirl;
    out[i] = {
      sx: p1.x,
      sy: p1.y,
      ex: p2.x,
      ey: p2.y,
      mx: lerp(p1.x, p2.x, 0.5) + Math.cos(angle) * dist + midBias,
      my: lerp(p1.y, p2.y, 0.5) + Math.sin(angle) * dist - dist * 0.15,
      sr: p1.r,
      sg: p1.g,
      sb: p1.b,
      sa: p1.a,
      er: p2.r,
      eg: p2.g,
      eb: p2.b,
      ea: p2.a,
      size: CONFIG.particleSize * (0.75 + Math.random() * 0.9),
      twinkle: Math.random() * Math.PI * 2,
      delay: Math.random() * 0.18,
      spin: (Math.random() - 0.5) * 0.08,
      seed: Math.random() * 9999,
      holdAngle: Math.random() * Math.PI * 2,
      holdRadius: (Math.random() - 0.5) * CONFIG.holdDrift * 4,
    };
  }
  return out;
}

function buildGlints(): Glint[] {
  const arr: Glint[] = [];
  for (let i = 0; i < CONFIG.sparkleCount; i++) {
    arr.push({
      angle: Math.random() * Math.PI * 2,
      radius: 30 + Math.random() * 120,
      size: 0.8 + Math.random() * 2.8,
      speed: 0.4 + Math.random() * 1.2,
      phase: Math.random() * Math.PI * 2,
      hueMix: Math.random(),
    });
  }
  return arr;
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => {
      // CORS rejected — retry without crossOrigin so drawImage still works
      // (canvas will be tainted, but display is fine; particle sampling
      //  will fall back to placeholder colours in imageToParticlePoints)
      const fallback = new Image();
      fallback.onload = () => resolve(fallback);
      fallback.onerror = () => reject(new Error('Image failed to load'));
      fallback.src = src;
    };
    img.src = src;
  });
}

// ── Drawing helpers ─────────────────────────────────────────────────
function drawBackdrop(
  ctx: CanvasRenderingContext2D,
  baseW: number,
  baseH: number,
  progress: number,
) {
  const cx = baseW / 2,
    cy = baseH / 2;
  ctx.save();
  const aura = 0.12 + Math.sin(progress * Math.PI) * 0.25;
  const rg = ctx.createRadialGradient(
    cx,
    cy,
    12,
    cx,
    cy,
    Math.max(baseW, baseH) * 0.42,
  );
  rg.addColorStop(0, `rgba(255,214,1,${0.18 * aura * 4})`);
  rg.addColorStop(0.45, `rgba(115,200,255,${0.11 * aura * 3})`);
  rg.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = rg;
  ctx.fillRect(0, 0, baseW, baseH);
  ctx.restore();
}

function drawImageGhost(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  baseW: number,
  baseH: number,
  alpha: number,
  blurPx = 1.5,
) {
  if (!img || alpha <= 0.004) return;
  const fit = coverRect(img.width, img.height, baseW, baseH);
  const x = (baseW - fit.w) / 2,
    y = (baseH - fit.h) / 2;
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.filter = `blur(${blurPx}px)`;
  ctx.drawImage(img, x, y, fit.w, fit.h);
  ctx.restore();
}

function drawEnergyCloud(
  ctx: CanvasRenderingContext2D,
  baseW: number,
  baseH: number,
  progress: number,
  timeSec: number,
) {
  if (progress <= 0.001) return;
  const cx = baseW / 2,
    cy = baseH / 2;
  ctx.save();
  ctx.globalCompositeOperation = 'source-over';
  ctx.globalAlpha = 0.25;
  for (let i = 0; i < 10; i++) {
    const a = timeSec * (0.25 + i * 0.03) + i * 0.7;
    const rx = 90 + i * 10 + Math.sin(timeSec + i) * 6;
    const ry = 40 + i * 6 + Math.cos(timeSec * 0.8 + i) * 4;
    ctx.beginPath();
    ctx.ellipse(
      cx + Math.cos(a) * 8,
      cy + Math.sin(a * 1.3) * 6,
      rx,
      ry,
      a * 0.6,
      0,
      Math.PI * 2,
    );
    ctx.fillStyle =
      i % 2
        ? `rgba(255,214,1,${0.014 * progress})`
        : `rgba(120,210,255,${0.016 * progress})`;
    ctx.fill();
  }
  ctx.globalAlpha = 1;
  ctx.restore();
}

function drawGlintsFrame(
  ctx: CanvasRenderingContext2D,
  glints: Glint[],
  baseW: number,
  baseH: number,
  progress: number,
  timeSec: number,
) {
  if (progress <= 0.001) return;
  const cx = baseW / 2,
    cy = baseH / 2;
  ctx.save();
  ctx.globalCompositeOperation = 'source-over';
  for (const g of glints) {
    const a = g.angle + timeSec * g.speed + Math.sin(timeSec + g.phase) * 0.2;
    const r = g.radius + Math.sin(timeSec * 2 + g.phase) * 10;
    const x = cx + Math.cos(a) * r;
    const y = cy + Math.sin(a) * r * 0.6;
    const pulse = 0.35 + 0.65 * Math.sin(timeSec * 6 + g.phase) ** 2;
    const alpha = progress * pulse * 0.9;
    ctx.beginPath();
    ctx.fillStyle =
      g.hueMix > 0.5
        ? `rgba(255,214,1,${alpha * 0.55})`
        : `rgba(120,210,255,${alpha * 0.45})`;
    ctx.arc(x, y, g.size * (0.8 + pulse), 0, Math.PI * 2);
    ctx.fill();
    if (alpha > 0.15) {
      ctx.strokeStyle = `rgba(0,13,38,${alpha * 0.18})`;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(x - 3, y);
      ctx.lineTo(x + 3, y);
      ctx.moveTo(x, y - 3);
      ctx.lineTo(x, y + 3);
      ctx.stroke();
    }
  }
  ctx.restore();
}

function drawParticlesDissolve(
  ctx: CanvasRenderingContext2D,
  particles: Particle[],
  progress: number,
  timeSec: number,
) {
  ctx.save();
  ctx.globalCompositeOperation = 'source-over';
  for (const p of particles) {
    const t = clamp((progress - p.delay) / (1 - p.delay), 0, 1);
    const moveT = easeInOutCubic(t) * 0.5;
    const fadeIn = smoothstep(0.03, 0.24, t);
    const oldFade = 1 - smoothstep(0.14, 0.82, t);
    const x =
      quadBezier(p.sx, p.mx, p.ex, moveT) +
      Math.sin(timeSec * 2.3 + p.seed) * p.spin * 18;
    const y =
      quadBezier(p.sy, p.my, p.ey, moveT) +
      Math.cos(timeSec * 2.0 + p.seed) * p.spin * 18;
    const alpha = clamp((p.sa * oldFade + 0.25) * fadeIn, 0, 1);
    const sizeBoost = 1 + bell(t, 0.18, 0.78) * 1.45;
    const sparkle = 0.85 + Math.sin(timeSec * 7 + p.twinkle) * 0.15;
    const size = p.size * sizeBoost * sparkle;
    if (alpha <= 0.004) continue;
    ctx.beginPath();
    ctx.fillStyle = `rgba(${p.sr},${p.sg},${p.sb},${alpha})`;
    ctx.arc(x, y, size, 0, Math.PI * 2);
    ctx.fill();
    if (alpha > 0.14) {
      ctx.beginPath();
      ctx.fillStyle = `rgba(0,13,38,${alpha * 0.08})`;
      ctx.arc(x, y, size * 2.0, 0, Math.PI * 2);
      ctx.fill();
    }
  }
  ctx.restore();
}

function drawParticlesHold(
  ctx: CanvasRenderingContext2D,
  particles: Particle[],
  progress: number,
  timeSec: number,
) {
  ctx.save();
  ctx.globalCompositeOperation = 'source-over';
  for (const p of particles) {
    const baseX =
      quadBezier(p.sx, p.mx, p.ex, 0.5) +
      Math.sin(timeSec * 2.3 + p.seed) * p.spin * 18;
    const baseY =
      quadBezier(p.sy, p.my, p.ey, 0.5) +
      Math.cos(timeSec * 2.0 + p.seed) * p.spin * 18;
    const driftBlend = smoothstep(0.0, 0.18, progress);
    const driftX =
      (Math.cos(timeSec * 0.9 + p.holdAngle + p.seed * 0.001) * p.holdRadius +
        Math.sin(timeSec * 1.7 + p.seed * 0.002) * CONFIG.holdDrift) *
      driftBlend;
    const driftY =
      (Math.sin(timeSec * 1.1 + p.holdAngle + p.seed * 0.001) *
        p.holdRadius *
        0.6 +
        Math.cos(timeSec * 1.5 + p.seed * 0.002) * CONFIG.holdDrift * 0.65) *
      driftBlend;
    const x = baseX + driftX;
    const y = baseY + driftY;
    const alpha = 0.22 + (Math.sin(timeSec * 5 + p.twinkle) * 0.5 + 0.5) * 0.28;
    const size =
      p.size * (1.0 + (Math.sin(timeSec * 6 + p.twinkle) * 0.5 + 0.5) * 0.9);
    ctx.beginPath();
    ctx.fillStyle = `rgba(${p.sr},${p.sg},${p.sb},${alpha})`;
    ctx.arc(x, y, size, 0, Math.PI * 2);
    ctx.fill();
    if (alpha > 0.16) {
      ctx.beginPath();
      ctx.fillStyle = `rgba(0,13,38,${alpha * 0.08})`;
      ctx.arc(x, y, size * 1.9, 0, Math.PI * 2);
      ctx.fill();
    }
  }
  ctx.restore();
}

function drawParticlesReform(
  ctx: CanvasRenderingContext2D,
  particles: Particle[],
  progress: number,
  timeSec: number,
) {
  ctx.save();
  ctx.globalCompositeOperation = 'source-over';
  for (const p of particles) {
    const t = easeInOutCubic(progress);
    const startX =
      quadBezier(p.sx, p.mx, p.ex, 0.5) +
      Math.sin(timeSec * 2.3 + p.seed) * p.spin * 18;
    const startY =
      quadBezier(p.sy, p.my, p.ey, 0.5) +
      Math.cos(timeSec * 2.0 + p.seed) * p.spin * 18;
    const x = quadBezier(startX, p.mx, p.ex, t);
    const y = quadBezier(startY, p.my, p.ey, t);
    const r = Math.round(lerp(p.sr, p.er, t));
    const g = Math.round(lerp(p.sg, p.eg, t));
    const b = Math.round(lerp(p.sb, p.eb, t));
    const particleVisible = 1 - smoothstep(0.55, 1.0, progress);
    const appear = smoothstep(0.0, 0.28, progress);
    const alpha = clamp((0.22 + p.ea * 0.45) * particleVisible * appear, 0, 1);
    const sizeBoost = 1 + bell(progress, 0.0, 0.45) * 1.35;
    const sparkle = 0.88 + Math.sin(timeSec * 7 + p.twinkle) * 0.12;
    const size = p.size * sizeBoost * sparkle;
    if (alpha <= 0.004) continue;
    ctx.beginPath();
    ctx.fillStyle = `rgba(${r},${g},${b},${alpha})`;
    ctx.arc(x, y, size, 0, Math.PI * 2);
    ctx.fill();
    if (alpha > 0.14) {
      ctx.beginPath();
      ctx.fillStyle = `rgba(0,13,38,${alpha * 0.08})`;
      ctx.arc(x, y, size * 2.0, 0, Math.PI * 2);
      ctx.fill();
    }
  }
  ctx.restore();
}

// ── Component ───────────────────────────────────────────────────────
const DreamieTransition = forwardRef<DreamieTransitionHandle, Props>(
  (
    {
      imageUrl,
      alt,
      onReformComplete,
      width = 293,
      height = 440,
      startHidden = false,
    },
    ref,
  ) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const stateRef = useRef<{
      phase: 'idle' | 'dissolve' | 'hold' | 'reform';
      phaseStart: number;
      holdStart: number;
      particles: Particle[];
      glints: Glint[];
      oldImg: HTMLImageElement | null;
      newImg: HTMLImageElement | null;
      rafId: number | null;
      currentStaticImg: HTMLImageElement | null;
      pendingReformUrl: string | null;
    }>({
      phase: 'idle',
      phaseStart: 0,
      holdStart: 0,
      particles: [],
      glints: [],
      oldImg: null,
      newImg: null,
      rafId: null,
      currentStaticImg: null,
      pendingReformUrl: null as string | null,
    });

    const [animating, setAnimating] = useState(startHidden);
    const holdWorkflowRef = useRef(startHidden);
    const waitingForSwapRef = useRef(false);

    const onReformCompleteRef = useRef(onReformComplete);
    onReformCompleteRef.current = onReformComplete;

    const DPR =
      typeof window !== 'undefined'
        ? Math.max(1, Math.min(window.devicePixelRatio || 1, 2))
        : 1;

    // Pre-load image for canvas use (needed for dissolve particle sampling)
    useEffect(() => {
      const s = stateRef.current;
      if (s.phase !== 'idle') return;
      loadImage(imageUrl)
        .then((img) => {
          s.currentStaticImg = img;
        })
        .catch(() => {});
    }, [imageUrl]);

    const loop = useCallback(
      (now: number) => {
        const s = stateRef.current;
        if (s.phase === 'idle') return;

        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d', { alpha: true });
        if (!ctx) return;
        ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
        ctx.clearRect(0, 0, width + 2 * CANVAS_PAD, height + 2 * CANVAS_PAD);
        ctx.translate(CANVAS_PAD, CANVAS_PAD);

        const timeSec = now / 1000;

        if (s.phase === 'dissolve') {
          const t = clamp((now - s.phaseStart) / CONFIG.dissolveMs, 0, 1);
          drawBackdrop(ctx, width, height, 0.1 + t * 0.55);
          const oldAlpha = 1 - smoothstep(0.08, 0.72, t) * 0.94;
          if (s.oldImg)
            drawImageGhost(ctx, s.oldImg, width, height, oldAlpha, 0.6);
          drawEnergyCloud(ctx, width, height, 0.22 + t * 0.45, timeSec);
          drawGlintsFrame(
            ctx,
            s.glints,
            width,
            height,
            0.18 + t * 0.35,
            timeSec,
          );
          drawParticlesDissolve(ctx, s.particles, t, timeSec);
          if (t >= 1) {
            s.phase = 'hold';
            s.holdStart = now;
            s.phaseStart = now;
            // If reformTo was called during dissolve, trigger it now
            if (s.pendingReformUrl) {
              const url = s.pendingReformUrl;
              s.pendingReformUrl = null;
              loadImage(url)
                .then((newImg) => {
                  s.newImg = newImg;
                  if (s.oldImg) {
                    s.particles = buildMorphParticles(
                      s.oldImg,
                      newImg,
                      width,
                      height,
                    );
                  }
                  s.glints = buildGlints();
                  s.phase = 'reform';
                  s.phaseStart = performance.now();
                })
                .catch(() => {});
            }
          }
        } else if (s.phase === 'hold') {
          const holdElapsed = now - s.holdStart;
          const holdT = clamp(holdElapsed / CONFIG.autoHoldMs, 0, 1);
          drawBackdrop(ctx, width, height, 0.72);
          drawEnergyCloud(ctx, width, height, 0.78, timeSec);
          drawGlintsFrame(ctx, s.glints, width, height, 0.8, timeSec);
          drawParticlesHold(ctx, s.particles, holdT, timeSec);
        } else if (s.phase === 'reform') {
          const t = clamp((now - s.phaseStart) / CONFIG.reformMs, 0, 1);
          drawBackdrop(ctx, width, height, 0.78 - t * 0.32);
          drawEnergyCloud(ctx, width, height, 0.72 - t * 0.18, timeSec);
          drawGlintsFrame(
            ctx,
            s.glints,
            width,
            height,
            0.72 - t * 0.16,
            timeSec,
          );
          drawParticlesReform(ctx, s.particles, t, timeSec);
          if (s.newImg) {
            const newAlpha = smoothstep(0.22, 0.86, t) * 0.9;
            drawImageGhost(ctx, s.newImg, width, height, newAlpha, 0.5);
          }
          if (t >= 1) {
            s.phase = 'idle';
            if (s.newImg) {
              s.currentStaticImg = s.newImg;
            }
            // Draw final static frame — canvas stays visible until <img> loads
            ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
            ctx.clearRect(
              0,
              0,
              width + 2 * CANVAS_PAD,
              height + 2 * CANVAS_PAD,
            );
            ctx.translate(CANVAS_PAD, CANVAS_PAD);
            if (s.newImg) {
              const fit = coverRect(
                s.newImg.width,
                s.newImg.height,
                width,
                height,
              );
              ctx.drawImage(
                s.newImg,
                (width - fit.w) / 2,
                (height - fit.h) / 2,
                fit.w,
                fit.h,
              );
            }
            s.rafId = null;
            // Don't hide canvas yet — <img> onLoad will trigger the swap
            waitingForSwapRef.current = true;
            // Fallback: hide canvas after 500ms if onLoad doesn't fire
            setTimeout(() => {
              if (waitingForSwapRef.current) {
                waitingForSwapRef.current = false;
                setAnimating(false);
              }
            }, 500);
            onReformCompleteRef.current?.();
            return;
          }
        }

        s.rafId = requestAnimationFrame(loop);
      },
      [width, height, DPR],
    );

    const startLoop = useCallback(() => {
      const s = stateRef.current;
      if (s.rafId) cancelAnimationFrame(s.rafId);
      s.rafId = requestAnimationFrame(loop);
    }, [loop]);

    useImperativeHandle(
      ref,
      () => ({
        dissolve: () => {
          const s = stateRef.current;
          if (s.phase !== 'idle' || !s.currentStaticImg) return;
          s.oldImg = s.currentStaticImg;
          // Build particles using old image for both (new image not yet known)
          s.particles = buildMorphParticles(s.oldImg, s.oldImg, width, height);
          s.glints = buildGlints();
          s.phase = 'dissolve';
          s.phaseStart = performance.now();
          s.newImg = null;
          setAnimating(true);
          startLoop();
        },
        reformTo: (url: string) => {
          const s = stateRef.current;
          if (s.phase === 'idle') {
            if (holdWorkflowRef.current) {
              // startInHold may be loading — queue for when hold begins
              s.pendingReformUrl = url;
            }
            return;
          }
          if (s.phase !== 'hold' && s.phase !== 'dissolve') return;
          if (s.phase === 'dissolve') {
            // Queue — will be picked up when dissolve finishes
            s.pendingReformUrl = url;
            return;
          }
          loadImage(url)
            .then((newImg) => {
              s.newImg = newImg;
              // Rebuild particles with proper old→new mapping
              if (s.oldImg) {
                s.particles = buildMorphParticles(
                  s.oldImg,
                  newImg,
                  width,
                  height,
                );
              }
              s.glints = buildGlints();
              s.phase = 'reform';
              s.phaseStart = performance.now();
            })
            .catch(() => {});
        },
        startInHold: () => {
          const s = stateRef.current;
          if (s.phase !== 'idle') return;
          const img = s.currentStaticImg;
          if (!img) {
            // Image not pre-loaded yet — load it first, then enter hold
            loadImage(imageUrl)
              .then((loaded) => {
                s.currentStaticImg = loaded;
                s.oldImg = loaded;
                s.particles = buildMorphParticles(
                  loaded,
                  loaded,
                  width,
                  height,
                );
                s.glints = buildGlints();
                s.phase = 'hold';
                s.holdStart = performance.now();
                s.phaseStart = performance.now();
                s.newImg = null;
                setAnimating(true);
                startLoop();
                // Check for a pending reform queued before hold was ready
                if (s.pendingReformUrl) {
                  const pending = s.pendingReformUrl;
                  s.pendingReformUrl = null;
                  loadImage(pending)
                    .then((newImg) => {
                      s.newImg = newImg;
                      if (s.oldImg) {
                        s.particles = buildMorphParticles(
                          s.oldImg,
                          newImg,
                          width,
                          height,
                        );
                      }
                      s.glints = buildGlints();
                      s.phase = 'reform';
                      s.phaseStart = performance.now();
                    })
                    .catch(() => {});
                }
              })
              .catch(() => {});
            return;
          }
          s.oldImg = img;
          s.particles = buildMorphParticles(img, img, width, height);
          s.glints = buildGlints();
          s.phase = 'hold';
          s.holdStart = performance.now();
          s.phaseStart = performance.now();
          s.newImg = null;
          setAnimating(true);
          startLoop();
          // Check for a pending reform queued before hold was ready
          if (s.pendingReformUrl) {
            const pending = s.pendingReformUrl;
            s.pendingReformUrl = null;
            loadImage(pending)
              .then((newImg) => {
                s.newImg = newImg;
                if (s.oldImg) {
                  s.particles = buildMorphParticles(
                    s.oldImg,
                    newImg,
                    width,
                    height,
                  );
                }
                s.glints = buildGlints();
                s.phase = 'reform';
                s.phaseStart = performance.now();
              })
              .catch(() => {});
          }
        },
      }),
      [width, height, startLoop],
    );

    // Cleanup on unmount
    useEffect(() => {
      return () => {
        const s = stateRef.current;
        if (s.rafId) cancelAnimationFrame(s.rafId);
      };
    }, []);

    return (
      <>
        <img
          src={imageUrl}
          alt={alt}
          className='assistant-image'
          onLoad={() => {
            if (waitingForSwapRef.current) {
              waitingForSwapRef.current = false;
              setAnimating(false);
            }
          }}
          style={{
            opacity: animating ? 0 : 1,
          }}
        />
        <canvas
          ref={canvasRef}
          width={(width + 2 * CANVAS_PAD) * DPR}
          height={(height + 2 * CANVAS_PAD) * DPR}
          style={{
            position: 'absolute',
            top: -CANVAS_PAD,
            left: -CANVAS_PAD,
            width: width + 2 * CANVAS_PAD,
            height: height + 2 * CANVAS_PAD,
            opacity: animating ? 1 : 0,
            pointerEvents: 'none',
          }}
          aria-label={alt}
        />
      </>
    );
  },
);

DreamieTransition.displayName = 'DreamieTransition';
export default DreamieTransition;
